// src/pages/CollectionOrders/components/tables/OrderBatchesTab.tsx
import React, { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import DefaultModal from '../../../../components/default/DefaultModal';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import useCollectionOrderService from '../../../../app/services/hooks/useCollectionOrderService';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import { CollectionOrderBatchRow } from '../../../../app/models/CollectionOrder';
import BatchStatusChange from '../BatchStatusChange';

function getAvailableBatchesFromResponse(resp: ServiceResponse | undefined): SelectOpt[] {
  const data = resp?.data;
  if (!data) return [];
  const raw = Array.isArray(data.available_batches)
    ? data.available_batches
    : Array.isArray(data)
    ? data
    : [];
  // Solo incluir ítems con id válido para que el select muestre "Lote #ID" y no "null"
  return raw.filter(
    (a: SelectOpt) => a != null && (a.id !== undefined && a.id !== null && a.id !== '')
  );
}

type SelectOpt = { id: number | string; name: string; project_id?: number; unit_id?: number };

interface Props {
  orderId: number;
  rows: CollectionOrderBatchRow[];
  onChanged: () => void;
  readOnly?: boolean;
}

const OrderBatchesTab = ({ orderId, rows, onChanged, readOnly = false }: Props) => {
  const {
    getOrderBatches,
    createOrderBatchOptions,
    storeOrderBatch,
    storeOrderBatchByBarcode,
    deleteOrderBatch,
    fetchingStoreOrderBatch,
    fetchingStoreOrderBatchByBarcode,
    updateBatchStatus,
    fetchingUpdateBatchStatus,
  } = useCollectionOrderService();
  // Removido: fetchingGetOrderBatches (no se usa)

  const [showCreate, setShowCreate] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [available, setAvailable] = useState<SelectOpt[]>([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState<number[]>([]);
  const [barcodeValue, setBarcodeValue] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const [statusSelectedBatchId, setStatusSelectedBatchId] = useState<number | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const reload = () => {
    getOrderBatches(orderId, {
      onSuccess: () => onChanged(),
      onError: () => onChanged()
    });
  };

  const openCreate = () => {
    setShowCreate(true);
    setAvailable([]);
    setSelectedBatchIds([]);
    setError(null);
    setLoadingBatches(true);
    const timeoutId = window.setTimeout(() => setLoadingBatches(false), 12000);
    createOrderBatchOptions(orderId, {
      onSuccess: (resp: ServiceResponse) => {
        window.clearTimeout(timeoutId);
        setAvailable(getAvailableBatchesFromResponse(resp));
        setLoadingBatches(false);
      },
      onError: (resp: ServiceResponse) => {
        window.clearTimeout(timeoutId);
        setLoadingBatches(false);
        toast.error(resp?.message || 'No se pudieron cargar los lotes disponibles.');
      }
    });
  };

  const openBarcodeModal = () => {
    setBarcodeValue('');
    setError(null);
    setShowBarcodeModal(true);
  };

  const openStatusModal = () => {
    setStatusSelectedBatchId(null);
    setStatusError(null);
    setShowStatusModal(true);
  };

  const doStore = () => {
    if (!selectedBatchIds.length) {
      setError('Debe seleccionar al menos un lote');
      return;
    }
    const ids = [...selectedBatchIds];
    const addNext = (index: number) => {
      if (index >= ids.length) {
        setShowCreate(false);
        setSelectedBatchIds([]);
        reload();
        return;
      }
      storeOrderBatch(orderId, { batch_id: ids[index] }, {
        onSuccess: () => addNext(index + 1),
        onError: (resp: ServiceResponse) => setError(resp.message || 'Error al agregar un lote.')
      });
    };
    addNext(0);
  };

  const doStoreByBarcode = () => {
    const barcode = barcodeValue.trim().toUpperCase();
    if (!barcode) {
      setError('Debe escanear o ingresar un código de barras');
      return;
    }

    storeOrderBatchByBarcode(orderId, { barcode }, {
      onSuccess: () => {
        setShowBarcodeModal(false);
        setBarcodeValue('');
        setError(null);
        reload();
      },
      onError: (resp: ServiceResponse) => {
        setError(resp.message);
      }
    });
  };

  // Auto-focus en el input cuando se abre el modal
  useEffect(() => {
    if (showBarcodeModal && barcodeInputRef.current) {
      setTimeout(() => barcodeInputRef.current?.focus(), 100);
    }
  }, [showBarcodeModal]);

  // Manejar Enter en el input de código de barras
  const handleBarcodeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doStoreByBarcode();
    }
  };

  const handleConfirmStatusChange = () => {
    if (statusSelectedBatchId == null) {
      setStatusError('Debe seleccionar un lote.');
      return;
    }

    const target = rows.find((r) => r.batch_id === statusSelectedBatchId);
    if (!target) {
      setStatusError('No se encontró un lote asignado a la orden con ese ID.');
      return;
    }

    if (target.status !== 'ASSIGNED') {
      setStatusError('El lote debe estar en estado Asignado para marcarlo como Acopiado.');
      return;
    }

    updateBatchStatus(target.id, 'COLLECTED', {
      onSuccess: (resp: ServiceResponse) => {
        toast.success(resp.message || 'Lote marcado como Acopiado correctamente.');
        setShowStatusModal(false);
        setStatusError(null);
        setStatusSelectedBatchId(null);
        reload();
      },
      onError: (resp: ServiceResponse) => {
        setStatusError(resp.message || 'Ocurrió un error al actualizar el estado del lote.');
      },
    });
  };

  return (
    <>
      {!readOnly && (
        <div className="d-flex justify-content-between mb-2">
          {/* Acciones principales (lado izquierdo) */}
          <div className="d-flex gap-2">
            <button
              className="btn btn-success btn-sm"
              onClick={openBarcodeModal}
            >
              <i className="mdi mdi-barcode-scan me-1"></i>
              Escanear código de barras
            </button>
            <button
              className="btn btn-success btn-sm"
              onClick={openCreate}
            >
              <i className="fa fa-plus me-1" /> Agregar lote
            </button>
          </div>

          {/* Acción de cambio de estado (lado derecho) */}
          <div className="d-flex">
            <button
              className="btn btn-primary btn-sm"
              onClick={openStatusModal}
              disabled={rows.length === 0}
            >
              <i className="mdi mdi-warehouse me-1" />
              Acopiar lote
            </button>
          </div>
        </div>
      )}

      <div
        className="table-responsive"
        style={{
          minHeight: 200,
          maxHeight: rows.length > 4 ? 320 : undefined,
          overflowY: rows.length > 4 ? 'auto' : 'visible'
        }}
      >
        <table className="table table-sm table-striped mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Capacidad</th>
              <th>Unidad</th>
              <th>Actualizado por</th>
              <th style={{ minWidth: '90px' }}>Estado</th>
              {!readOnly && <th style={{ width: 100 }}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
            <tr key={r.id}>
                <td>
                  {r.batch_id != null ? (
                    <a href={`/batch/${r.batch_id}/edit`} className="text-decoration-underline">
                      {r.batch_id}
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                <td>{r.batch_capacity ?? ''}</td>
                <td>{r.batch_unit ?? ''}</td>
                <td>{r.status_updated_by_name ?? '-'}</td>
                <td>
                  {!readOnly ? (
                    <BatchStatusChange
                      linkId={r.id}
                      currentStatus={r.status}
                      currentStatusDisplay={r.status_display}
                      callbackSuccess={reload}
                    />
                  ) : (
                    <span>{r.status_display || r.status || '-'}</span>
                  )}
                </td>
                {!readOnly && (
                  <td>
                    <ButtonTableAction
                      title={r.status === 'COLLECTED' ? 'No se puede quitar un lote acopiado' : 'Quitar'}
                      classIcon="mdi mdi-delete"
                      colorIcon={r.status === 'COLLECTED' ? 'text-muted' : 'text-danger'}
                      locked={r.status === 'COLLECTED'}
                      errorMessage="No se puede eliminar un lote con estado Acopiado. Solo se pueden quitar lotes en estado Asignado o Sin utilizar."
                      callbackFunction={() =>
                        deleteOrderBatch(r.id, { onSuccess: reload, onError: reload })
                      }
                    />
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={readOnly ? 5 : 6} className="text-center text-muted">
                  Sin lotes asignados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal para agregar por selección (múltiples lotes) */}
      {!readOnly && showCreate && (
        <DefaultModal
          show={showCreate}
          handleClose={() => setShowCreate(false)}
          title="Agregar lotes a la orden"
          showFooter={false}
          backdrop={true}
        >
          <div className="mb-3">
            <label className="col-form-label">Lotes</label>
            <Select
              isMulti
              name="batch_ids"
              options={available
                .filter((a: SelectOpt) => a.id != null && a.id !== '')
                .map((a: SelectOpt) => {
                  const hasName = a.name != null && String(a.name).trim() !== '' && String(a.name) !== 'null';
                  return {
                    value: Number(a.id),
                    label: hasName ? `Lote #${a.id} - ${a.name}` : `Lote #${a.id}`
                  };
                })}
              value={available
                .filter((a: SelectOpt) => selectedBatchIds.includes(Number(a.id)))
                .map((a: SelectOpt) => {
                  const hasName = a.name != null && String(a.name).trim() !== '' && String(a.name) !== 'null';
                  return {
                    value: Number(a.id),
                    label: hasName ? `Lote #${a.id} - ${a.name}` : `Lote #${a.id}`
                  };
                })}
              onChange={(selectedOptions) => {
                setSelectedBatchIds(selectedOptions ? selectedOptions.map((o) => o.value) : []);
                setError(null);
              }}
              isDisabled={loadingBatches}
              placeholder={loadingBatches ? 'Cargando...' : 'Seleccione lotes...'}
              noOptionsMessage={() => 'No hay lotes disponibles'}
              className={error ? 'is-invalid' : ''}
              classNamePrefix="select"
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: error ? '#dc3545' : base.borderColor,
                  '&:hover': { borderColor: error ? '#dc3545' : base.borderColor }
                })
              }}
            />
            {!loadingBatches && (available || []).length === 0 && (
              <small className="text-muted d-block mt-1">
                No hay lotes disponibles para asignar (solo lotes de categoría Recolección del mismo proyecto y no asignados a otra orden).
              </small>
            )}
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>
          <div className="d-flex justify-content-end">
            <button
              className="btn btn-secondary me-2"
              onClick={() => setShowCreate(false)}
              disabled={!!fetchingStoreOrderBatch}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={doStore}
              disabled={!!fetchingStoreOrderBatch || selectedBatchIds.length === 0}
            >
              {fetchingStoreOrderBatch ? 'Agregando...' : `Agregar ${selectedBatchIds.length ? `(${selectedBatchIds.length})` : ''}`}
            </button>
          </div>
        </DefaultModal>
      )}

      {/* Modal para cambiar estado a Acopiado por ID de lote */}
      {!readOnly && showStatusModal && (
        <DefaultModal
          show={showStatusModal}
          handleClose={() => {
            if (!fetchingUpdateBatchStatus) {
              setShowStatusModal(false);
              setStatusError(null);
            }
          }}
          title="Marcar lote como Acopiado"
          showFooter={false}
          backdrop={true}
        >
          <div className="mb-3">
            <label className="col-form-label">Lote</label>
            <Select
              isMulti={false}
              name="status_batch_id"
              options={rows
                .filter((r) => r.status === 'ASSIGNED')
                .map((r) => ({
                  value: r.batch_id,
                  label: `Lote #${r.batch_id}`,
                }))}
              value={
                statusSelectedBatchId != null
                  ? {
                      value: statusSelectedBatchId,
                      label: `Lote #${statusSelectedBatchId}`,
                    }
                  : null
              }
              onChange={(option) => {
                setStatusSelectedBatchId(option ? Number((option as any).value) : null);
                setStatusError(null);
              }}
              isDisabled={fetchingUpdateBatchStatus}
              placeholder="Seleccione un lote..."
              noOptionsMessage={() => 'No hay lotes asignados en estado Asignado.'}
              className={statusError ? 'is-invalid' : ''}
              classNamePrefix="select"
            />
            {statusError && (
              <div className="invalid-feedback d-block">
                {statusError}
              </div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-light btn-sm"
              onClick={() => {
                if (!fetchingUpdateBatchStatus) {
                  setShowStatusModal(false);
                  setStatusError(null);
                }
              }}
              disabled={fetchingUpdateBatchStatus}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleConfirmStatusChange}
              disabled={fetchingUpdateBatchStatus}
            >
              {fetchingUpdateBatchStatus ? 'Guardando...' : 'Aceptar'}
            </button>
          </div>
        </DefaultModal>
      )}

      {/* Modal para agregar por código de barras */}
      {!readOnly && showBarcodeModal && (
        <DefaultModal
          show={showBarcodeModal}
          handleClose={() => setShowBarcodeModal(false)}
          title="Escanear código de barras del lote"
          showFooter={false}
          backdrop={true}
        >
          <div className="alert alert-info" role="alert">
            <i className="mdi mdi-information me-2"></i>
            Escanee el código de barras del lote
          </div>

          <div className="mb-3">
            <label className="col-form-label">
              <i className="mdi mdi-barcode-scan me-2"></i>
              Código de barras
            </label>
            <input
              ref={barcodeInputRef}
              type="text"
              className={`form-control form-control-lg ${error ? 'is-invalid' : ''}`}
              value={barcodeValue}
              onChange={(e) => {
                setBarcodeValue(e.target.value);
                setError(null);
              }}
              onKeyPress={handleBarcodeKeyPress}
              placeholder="BATCH-00000000"
              autoComplete="off"
              style={{
                fontSize: '1.2rem',
                letterSpacing: '0.1em',
                fontFamily: 'monospace'
              }}
            />
            <small className="form-text text-muted">
              El lote se agregará automáticamente al presionar Enter o hacer clic en Agregar
            </small>
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>

          <div className="d-flex justify-content-end">
            <button
              className="btn btn-secondary me-2"
              onClick={() => setShowBarcodeModal(false)}
              disabled={!!fetchingStoreOrderBatchByBarcode}
            >
              Cancelar
            </button>
            <button
              className="btn btn-success"
              onClick={doStoreByBarcode}
              disabled={!!fetchingStoreOrderBatchByBarcode || !barcodeValue.trim()}
            >
              <i className="mdi mdi-check me-1"></i>
              {fetchingStoreOrderBatchByBarcode ? 'Agregando...' : 'Agregar'}
            </button>
          </div>
        </DefaultModal>
      )}
    </>
  );
};

export default OrderBatchesTab;